import { useState } from 'react'
import { useSettingsStore } from '@/stores/settings'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { 
  Key, 
  Plus, 
  Copy, 
  RefreshCw,
  Trash2,
  Eye,
  EyeOff,
  Webhook,
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import { toast } from 'sonner'

export function APISettings() {
  const { 
    api, 
    createAPIKey, 
    deleteAPIKey, 
    regenerateAPIKey,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    loading 
  } = useSettingsStore()
  
  const [showNewKeyForm, setShowNewKeyForm] = useState(false)
  const [showNewWebhookForm, setShowNewWebhookForm] = useState(false)
  const [visibleKeys, setVisibleKeys] = useState<Record<string, boolean>>({})
  const [newKeyForm, setNewKeyForm] = useState({
    name: '',
    permissions: [] as string[]
  })
  const [newWebhookForm, setNewWebhookForm] = useState({
    name: '',
    url: '',
    events: [] as string[],
    active: true
  })

  const permissions = [
    'servers:read',
    'servers:write',
    'servers:delete',
    'analytics:read',
    'teams:read',
    'teams:write',
    'templates:read',
    'templates:write'
  ]

  const webhookEvents = [
    'server.created',
    'server.updated',
    'server.deleted',
    'server.started',
    'server.stopped',
    'alert.triggered',
    'deployment.success',
    'deployment.failure'
  ]

  const handleCreateAPIKey = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!newKeyForm.name.trim()) {
      toast.error('Please enter a name for the API key')
      return
    }
    
    if (newKeyForm.permissions.length === 0) {
      toast.error('Please select at least one permission')
      return
    }
    
    try {
      const newKey = await createAPIKey(newKeyForm.name, newKeyForm.permissions)
      setNewKeyForm({ name: '', permissions: [] })
      setShowNewKeyForm(false)
      toast.success('API key created successfully!')
      
      // Show the new key to the user
      navigator.clipboard.writeText(newKey)
      toast.info('API key copied to clipboard')
    } catch (error) {
      toast.error('Failed to create API key')
    }
  }

  const handleDeleteAPIKey = async (id: string) => {
    if (confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      try {
        await deleteAPIKey(id)
        toast.success('API key deleted')
      } catch (error) {
        toast.error('Failed to delete API key')
      }
    }
  }

  const handleRegenerateKey = async (id: string) => {
    if (confirm('Are you sure you want to regenerate this API key? The old key will stop working immediately.')) {
      try {
        const newKey = await regenerateAPIKey(id)
        navigator.clipboard.writeText(newKey)
        toast.success('API key regenerated and copied to clipboard')
      } catch (error) {
        toast.error('Failed to regenerate API key')
      }
    }
  }

  const handleCreateWebhook = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!newWebhookForm.name.trim() || !newWebhookForm.url.trim()) {
      toast.error('Please fill in all required fields')
      return
    }
    
    if (newWebhookForm.events.length === 0) {
      toast.error('Please select at least one event')
      return
    }
    
    try {
      await createWebhook(newWebhookForm)
      setNewWebhookForm({ name: '', url: '', events: [], active: true })
      setShowNewWebhookForm(false)
      toast.success('Webhook created successfully!')
    } catch (error) {
      toast.error('Failed to create webhook')
    }
  }

  const handleToggleWebhook = async (id: string, active: boolean) => {
    try {
      await updateWebhook(id, { active })
      toast.success(`Webhook ${active ? 'enabled' : 'disabled'}`)
    } catch (error) {
      toast.error('Failed to update webhook')
    }
  }

  const handleDeleteWebhook = async (id: string) => {
    if (confirm('Are you sure you want to delete this webhook?')) {
      try {
        await deleteWebhook(id)
        toast.success('Webhook deleted')
      } catch (error) {
        toast.error('Failed to delete webhook')
      }
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  const toggleKeyVisibility = (keyId: string) => {
    setVisibleKeys(prev => ({ ...prev, [keyId]: !prev[keyId] }))
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const handlePermissionToggle = (permission: string) => {
    setNewKeyForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }))
  }

  const handleEventToggle = (event: string) => {
    setNewWebhookForm(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }))
  }

  return (
    <div className="space-y-6">
      {/* API Keys */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Key className="h-5 w-5" />
              <span>API Keys</span>
            </div>
            <Button onClick={() => setShowNewKeyForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create API Key
            </Button>
          </CardTitle>
          <CardDescription>
            Manage API keys for programmatic access to your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {api.api_keys.length === 0 ? (
              <div className="text-center py-8">
                <Key className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold">No API Keys</h3>
                <p className="text-muted-foreground">Create your first API key to get started</p>
              </div>
            ) : (
              api.api_keys.map((key) => (
                <div key={key.id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{key.name}</div>
                      <div className="text-sm text-muted-foreground">
                        Created: {formatDate(key.created_at)}
                        {key.last_used && ` • Last used: ${formatDate(key.last_used)}`}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRegenerateKey(key.id)}
                        disabled={loading}
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Regenerate
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteAPIKey(key.id)}
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-muted p-2 rounded font-mono text-sm">
                      {visibleKeys[key.id] ? key.key : '•'.repeat(32)}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleKeyVisibility(key.id)}
                    >
                      {visibleKeys[key.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(key.key)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {key.permissions.map((permission) => (
                      <Badge key={permission} variant="outline">
                        {permission}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))
            )}

            {/* New API Key Form */}
            {showNewKeyForm && (
              <form onSubmit={handleCreateAPIKey} className="p-4 border rounded-lg bg-muted/50">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="key-name">Name</Label>
                    <Input
                      id="key-name"
                      placeholder="My API Key"
                      value={newKeyForm.name}
                      onChange={(e) => setNewKeyForm(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Permissions</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {permissions.map((permission) => (
                        <div key={permission} className="flex items-center space-x-2">
                          <Switch
                            checked={newKeyForm.permissions.includes(permission)}
                            onCheckedChange={() => handlePermissionToggle(permission)}
                          />
                          <Label className="text-sm">{permission}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setShowNewKeyForm(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={loading}>
                      {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                      Create API Key
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Webhooks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Webhook className="h-5 w-5" />
              <span>Webhooks</span>
            </div>
            <Button onClick={() => setShowNewWebhookForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Webhook
            </Button>
          </CardTitle>
          <CardDescription>
            Configure webhooks to receive notifications about events
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {api.webhooks.length === 0 ? (
              <div className="text-center py-8">
                <Webhook className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold">No Webhooks</h3>
                <p className="text-muted-foreground">Create your first webhook to receive event notifications</p>
              </div>
            ) : (
              api.webhooks.map((webhook) => (
                <div key={webhook.id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium flex items-center space-x-2">
                        <span>{webhook.name}</span>
                        {webhook.active ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-orange-500" />
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {webhook.url}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={webhook.active}
                        onCheckedChange={(checked) => handleToggleWebhook(webhook.id, checked)}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteWebhook(webhook.id)}
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {webhook.events.map((event) => (
                      <Badge key={event} variant="outline">
                        {event}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))
            )}

            {/* New Webhook Form */}
            {showNewWebhookForm && (
              <form onSubmit={handleCreateWebhook} className="p-4 border rounded-lg bg-muted/50">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="webhook-name">Name</Label>
                      <Input
                        id="webhook-name"
                        placeholder="My Webhook"
                        value={newWebhookForm.name}
                        onChange={(e) => setNewWebhookForm(prev => ({ ...prev, name: e.target.value }))}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="webhook-url">URL</Label>
                      <Input
                        id="webhook-url"
                        type="url"
                        placeholder="https://example.com/webhook"
                        value={newWebhookForm.url}
                        onChange={(e) => setNewWebhookForm(prev => ({ ...prev, url: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Events</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {webhookEvents.map((event) => (
                        <div key={event} className="flex items-center space-x-2">
                          <Switch
                            checked={newWebhookForm.events.includes(event)}
                            onCheckedChange={() => handleEventToggle(event)}
                          />
                          <Label className="text-sm">{event}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setShowNewWebhookForm(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={loading}>
                      {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                      Create Webhook
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
} 